export const FR_HOST = 'crownsportnutrition.fr';
export const PROJECT_KEY = 'phc_MVKivSLULgWAS0ctiMC5zCLQ3IA02wPmuamZQULtplJ';
export const CONTEXT_COOKIE = 'crown_fr_posthog';
const SENSITIVE_PATH = /\/(?:account|accounts|checkouts?|orders|challenge|password|authentication)(?:\/|$)/i;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

export function publicFrenchPage(href) {
  try {
    const url = new URL(href);
    return url.protocol === 'https:' && url.hostname === FR_HOST && !SENSITIVE_PATH.test(decodeURIComponent(url.pathname));
  } catch { return false; }
}

export function cleanUrl(value) {
  try {
    const url = new URL(value, `https://${FR_HOST}`);
    if (!['https:', 'http:'].includes(url.protocol)) return '';
    if (url.hostname !== FR_HOST) return url.origin;
    const path = decodeURIComponent(url.pathname);
    return url.origin + (SENSITIVE_PATH.test(path) ? '/[private]' : url.pathname);
  } catch { return ''; }
}

export function safeCampaign(href) {
  const result = {};
  try {
    const url = new URL(href);
    for (const key of UTM_KEYS) {
      const value = url.searchParams.get(key);
      if (value && value.length <= 120 && !/[@\r\n]|%40/i.test(value)) result[key] = value;
    }
  } catch { /* Invalid URLs do not produce attribution. */ }
  return result;
}

export function analyticsConsent(shopify) {
  try {
    const privacy = shopify?.customerPrivacy;
    return privacy?.analyticsProcessingAllowed() === true && privacy.currentVisitorConsent().analytics === 'yes';
  } catch { return false; }
}

export function cleanProperties(value, key = '') {
  if (typeof value === 'string') {
    if (/url|href|referrer/i.test(key) || /^https?:\/\//i.test(value)) return cleanUrl(value);
    return value;
  }
  if (Array.isArray(value)) return value.map((item) => cleanProperties(item, key));
  if (value && typeof value === 'object') {
    const result = {};
    for (const [name, item] of Object.entries(value)) {
      if (/^(?:email|phone|address|first_name|last_name|fbclid|gclid|fbc|fbp|\$initial_(?:gclid|fbclid))$/i.test(name)) continue;
      result[name] = cleanProperties(item, name);
    }
    return result;
  }
  return value;
}

export function cartChanges(previous, current) {
  const changes = [];
  const before = new Map(previous.map((item) => [item.key, item]));
  const after = new Map(current.map((item) => [item.key, item]));
  for (const key of new Set([...before.keys(), ...after.keys()])) {
    const old = before.get(key), next = after.get(key);
    const delta = (next?.quantity || 0) - (old?.quantity || 0);
    if (!delta) continue;
    const { key: ignored, ...item } = next || old;
    changes.push({ event: delta > 0 ? 'product_added_to_cart' : 'product_removed_from_cart', properties: { ...item, quantity: Math.abs(delta), value: Math.abs(delta) * item.price } });
  }
  return changes;
}

export function sdkConfig(allowed, loaded) {
  return {
    api_host: 'https://eu.i.posthog.com',
    ui_host: 'https://eu.posthog.com',
    person_profiles: 'identified_only',
    persistence: 'localStorage',
    persistence_name: 'crown_fr',
    cross_subdomain_cookie: false,
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: { dom_event_allowlist: ['click'] },
    mask_all_text: true,
    mask_all_element_attributes: true,
    mask_personal_data_properties: true,
    disable_capture_url_hashes: true,
    save_referrer: false,
    save_campaign_params: false,
    capture_performance: false,
    capture_heatmaps: false,
    capture_exceptions: false,
    capture_dead_clicks: false,
    rageclick: false,
    disable_surveys: true,
    disable_product_tours: true,
    disable_conversations: true,
    disable_web_experiments: true,
    enable_recording_console_log: false,
    ip: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      blockSelector: 'input, textarea, select, iframe, canvas, [contenteditable], .ph-no-capture, [data-ph-no-capture]',
      recordCrossOriginIframes: false,
      recordHeaders: false,
      recordBody: false,
      captureJsonLd: false,
      captureCanvas: { recordCanvas: false },
      maskCapturedNetworkRequestFn: () => null,
      maskAttributeFn: (name, value) => {
        if (/^(class|style|width|height|viewBox|d|fill|stroke)$/i.test(name)) return value;
        if (/^(src|href)$/i.test(name)) return cleanUrl(value);
        return '[masked]';
      },
    },
    before_send: (event) => allowed() ? { ...event, properties: cleanProperties(event.properties) } : null,
    loaded,
  };
}

export function startFrenchPosthog(win, doc, config, loadSdk = () => new Promise((resolve, reject) => {
  if (win.posthog?.init && !Array.isArray(win.posthog)) return resolve(win.posthog);
  const script = doc.createElement('script');
  script.src = 'https://eu-assets.i.posthog.com/static/array.js';
  script.async = true;
  script.onload = () => win.posthog?.init ? resolve(win.posthog) : reject(new Error('PostHog SDK unavailable'));
  script.onerror = reject;
  doc.head.appendChild(script);
})) {
  if (!publicFrenchPage(win.location.href) || win.Shopify?.designMode) return;
  let instance, loading, active = false, epoch = 0;
  let cart = config.cart || [];
  const allowed = () => publicFrenchPage(win.location.href) && analyticsConsent(win.Shopify);
  const expireContext = () => { doc.cookie = `${CONTEXT_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax; Secure`; };
  const writeContext = () => {
    if (!instance || !allowed()) return;
    const context = { origin: FR_HOST, id: instance.get_distinct_id(), session: instance.get_session_id(), expires: Date.now() + 1800000 };
    doc.cookie = `${CONTEXT_COOKIE}=${encodeURIComponent(JSON.stringify(context))}; Path=/; Max-Age=1800; SameSite=Lax; Secure`;
  };
  const capture = (event, properties = {}) => {
    if (!active || !instance || !allowed()) return;
    writeContext();
    instance.capture(event, { storefront_origin: FR_HOST, locale: config.locale, country: config.country, currency: config.currency, $current_url: cleanUrl(win.location.href), ...properties });
  };
  const activate = () => {
    if (!allowed() || active || !instance) return;
    active = true;
    instance.opt_in_capturing({ captureEventName: false });
    if (!config.customer_id && String(instance.get_distinct_id()).startsWith('shopify_customer:')) instance.reset();
    if (config.customer_id) instance.identify(`shopify_customer:${config.customer_id}`);
    instance.register({ storefront_origin: FR_HOST, locale: config.locale, country: config.country, currency: config.currency });
    const landing = cleanUrl(win.location.href);
    const campaign = safeCampaign(win.location.href);
    instance.register_once({ first_landing: landing, first_referrer: cleanUrl(doc.referrer), ...Object.fromEntries(UTM_KEYS.map((k) => [`first_${k}`, campaign[k] || ''])) });
    if (Object.keys(campaign).length) instance.register({ last_landing: landing, ...Object.fromEntries(UTM_KEYS.map((k) => [`last_${k}`, campaign[k] || ''])) });
    instance.startSessionRecording();
    capture('$pageview');
    if (config.page_type === 'product' && config.product) capture('product_viewed', config.product);
    if (config.page_type === 'collection') capture('collection_viewed', { collection_id: config.collection_id });
    if (config.page_type === 'cart') capture('cart_viewed', { items: cart.map(({ key, ...item }) => item) });
    if (config.page_type === 'search') capture('search_submitted');
  };
  const syncConsent = () => {
    if (!allowed()) {
      epoch += 1;
      active = false;
      expireContext();
      if (instance) {
        instance.stopSessionRecording();
        instance.opt_out_capturing();
      }
      return;
    }
    if (instance) return activate();
    if (loading) return;
    const loadEpoch = epoch;
    loading = loadSdk().then((sdk) => {
      if (loadEpoch !== epoch || !allowed()) return;
      const options = sdkConfig(allowed, (client) => { instance = client; activate(); });
      options.custom_personal_data_properties = Array.from(new URL(win.location.href).searchParams.keys());
      sdk.init(PROJECT_KEY, options, 'crown_fr');
    }).catch(() => {}).finally(() => {
      loading = null;
      if (loadEpoch !== epoch && allowed() && !instance) syncConsent();
    });
  };
  doc.addEventListener('visitorConsentCollected', syncConsent);
  doc.addEventListener('cart:update', (event) => {
    const items = event.detail?.resource?.items;
    if (!Array.isArray(items)) return;
    const next = items.map((item) => ({ key: item.key, product_id: String(item.product_id), variant_id: String(item.variant_id || item.id), sku: item.sku, name: item.product_title, quantity: item.quantity, price: Number(item.final_price) / 100 }));
    if (!event.detail?.data?.didError) for (const change of cartChanges(cart, next)) capture(change.event, change.properties);
    cart = next;
  });
  win.addEventListener('pagehide', writeContext);
  win.addEventListener('pageshow', syncConsent);
  if (win.Shopify?.customerPrivacy) syncConsent();
  else win.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => { if (!error) syncConsent(); });
  return { syncConsent };
}

if (typeof document !== 'undefined') {
  const element = document.getElementById('crown-fr-posthog-config');
  if (element) {
    try { startFrenchPosthog(window, document, JSON.parse(element.textContent)); } catch { /* Tracking must never interrupt shopping. */ }
  }
}
